/**
 * Offline-safe unique identifiers.
 *
 * ULIDs: a timestamp prefix plus randomness, so ids sort chronologically and
 * are unique without any server coordination. A monotonic counter is used in
 * the random tail so two ids generated in the same millisecond still sort
 * in creation order.
 *
 * `crypto.getRandomValues` is available in every browser 2.0 targets, and is
 * the only source of randomness — `Math.random` is not used anywhere in the
 * product, because a non-cryptographic id generator is a collision risk once
 * two windows generate ids at once.
 */

let lastTime = -1;
let counter = 0;

const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'; // Crockford base32
const TIME_LEN = 10;
const RANDOM_LEN = 16;

function encodeTime(now: number): string {
  let out = '';
  let t = now;
  for (let i = TIME_LEN - 1; i >= 0; i--) {
    out = ENCODING[t % 32] + out;
    t = Math.floor(t / 32);
  }
  return out;
}

function encodeRandom(): string {
  const bytes = new Uint8Array(RANDOM_LEN);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < RANDOM_LEN; i++) {
    out += ENCODING[(bytes[i] ?? 0) % 32];
  }
  return out;
}

/** A 26-character ULID. */
export function ulid(now = Date.now()): string {
  if (now === lastTime) {
    counter += 1;
  } else {
    lastTime = now;
    counter = 0;
  }
  // Fold the counter into the random tail so same-ms ids stay ordered.
  const tail = encodeRandom();
  const last = ENCODING.indexOf(tail[RANDOM_LEN - 1] ?? '0');
  const bumped =
    counter === 0
      ? tail
      : tail.slice(0, RANDOM_LEN - 1) + ENCODING[(last + counter) % 32];
  return encodeTime(now) + bumped;
}

/** ULID for a business record created now. */
export function newId(): string {
  return ulid();
}

/** The embedded creation time of a ULID, for diagnostics. */
export function ulidTime(id: string): Date | null {
  if (id.length !== TIME_LEN + RANDOM_LEN) return null;
  let t = 0;
  for (let i = 0; i < TIME_LEN; i++) {
    const v = ENCODING.indexOf(id[i] ?? '');
    if (v < 0) return null;
    t = t * 32 + v;
  }
  return new Date(t);
}
